import styled, { css } from 'styled-components';
import {
  TYPOGRAPHY,
  SPACINGS,
  COLORS,
  flex,
  Header,
  Div,
  P,
  HLink,
  Button,
  OnboardingThemeContextProvider,
  cssByBreakpoint,
} from '@figure/figure-frontlib-gui';
import { useRedirect, useLeaveSurveyModal, useTelemetry, useIdentity } from 'shared/hooks';
import phoneguy from 'shared/assets/images/guy-viewing-cellphone.svg';
import checkmark from 'shared/assets/images/checkmark-white.svg';
import fiona from 'shared/assets/images/fiona.svg';

const PurpleBox = styled.div`
  background-color: ${COLORS.PRIMARY_100};
  border: 1px solid ${COLORS.PRIMARY_500};
  border-radius: 4px;

  margin-bottom: 2rem;

  ${cssByBreakpoint([
    css`
      padding: 2rem 2rem 0 2rem;
    `,
    css`
      padding: 2rem 12rem 0 2rem;
    `,
  ])}
`;

const Legal = styled.p`
  ${cssByBreakpoint([
    css`
      ${TYPOGRAPHY.XS}
      margin: ${SPACINGS.M}px 0 ${SPACINGS.X6L}px 0;
      padding-right: ${SPACINGS.X3L}px;
    `,
    css`
      margin: ${SPACINGS.M}px 0;
      padding-right: ${SPACINGS.S}px;
    `,
  ])}
`;

const PurpleBody = styled.p`
  ${TYPOGRAPHY.S}
  padding-bottom: 1rem;
  color: ${COLORS.PRIMARY_500};
`;

const BaseContainer = styled.div`
  ${cssByBreakpoint([
    css`
      padding: 2rem;
      height: 100%;
      background-color: ${COLORS.BACKGROUND_1};
    `,
    css`
      height: 100vh;
    `,
  ])}
`;

const HeaderBlock = styled.div`
  ${cssByBreakpoint([
    css`
      padding: ${SPACINGS.X2L}px ${SPACINGS.M}px;
    `,
    css`
      border-bottom: solid 1px ${COLORS.PRIMARY_150};
      padding: ${SPACINGS.X2L}px;
    `,
  ])}
`;

const HdrTop = styled.p`
  ${TYPOGRAPHY.H5}
  padding-bottom: ${SPACINGS.S}px;

  ${cssByBreakpoint(css`
    font-size: 2.2rem;
  `)}
`;

const Content = styled.div`
  max-width: 840px;
  margin-left: auto;
  margin-right: auto;
`;

const MenuItem = styled.span`
  cursor: pointer;
  display: block;
  padding: ${SPACINGS.M}px ${SPACINGS.L}px;
`;

const FPayCSWrapper = styled.div`
  background-color: ${COLORS.WHITE};
`;

const FContainer = styled.div`
  ${flex('space-between', 'center', 'column')}

  ${cssByBreakpoint([
    css`
      flex-direction: column;
    `,
    css`
      flex-direction: row;
      gap: ${SPACINGS.X2L}px;
      margin-bottom: ${SPACINGS.X2L}px;
      padding-left: 3rem;
    `,
  ])}
`;

const Img = styled.img`
  ${cssByBreakpoint([
    css`
      display: none;
    `,
    css`
      height: 400px;
      display: block;
    `,
  ])}
`;

const UL = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${SPACINGS.X2L}px;
`;

const LI = styled.div`
  display: flex;
  padding-bottom: ${SPACINGS.M}px;
  font-size: 2.2rem;
  &::before {
    content: url(${checkmark});
    padding-right: ${SPACINGS.M}px;
  }
`;

const BulletsBody = styled.p`
  ${cssByBreakpoint([
    css`
      ${flex('center', 'flex-start', 'column')};
      padding: 0 ${SPACINGS.L}px;
    `,
    css`
      padding: 0;
    `,
  ])}
`;

const ButtonContainer = styled.div`
  ${cssByBreakpoint([
    css`
      width: 100%;
      margin-bottom: ${SPACINGS.L}px;
    `,
    css`
      margin-bottom: ${SPACINGS.S}px;
      max-width: 243px;
      width: 243px;
    `,
  ])}
`;

const DeclineFionaContainer = () => {
  const { redirectToDashboard } = useRedirect();
  const { resetSurvey } = useLeaveSurveyModal();
  const { logout } = useIdentity();
  const { telemetry } = useTelemetry();

  const handleDashboardClick = () => {
    redirectToDashboard();
  };

  const handleLogout = () => {
    logout();
    window.location.reload();
  };

  const redirectToFiona = () => {
    telemetry(`pl_fiona_redirect`);
    window.open('https://fiona.com/partner/figure/loans', '_blank', 'noopener,noreferrer');
  };

  const StaticBullets = () => (
    <div>
      <UL>
        {[
          'Borrow up to $100,000',
          'Loans for debt consolidation and more',
          'Money as soon as next business day',
          'Get matched with personalized loan offers in less than 60 seconds',
        ].map((bullettext) => (
          <LI>
            <Div
              $css={css`
                ${TYPOGRAPHY.M}
              `}
            >
              {bullettext}
            </Div>
          </LI>
        ))}
      </UL>
    </div>
  );

  return (
    <OnboardingThemeContextProvider>
      <BaseContainer>
        <Header
          menuList={[
            <MenuItem
              onClick={() => {
                // don't show exit survey
                resetSurvey();
                redirectToDashboard();
              }}
            >
              Dashboard
            </MenuItem>,
            <MenuItem onClick={handleLogout}>Sign out</MenuItem>,
          ]}
        />
        <Content>
          <PurpleBox>
            <PurpleBody>
              Unfortunately, we cannot offer you a Figure Personal Loan at this time.
            </PurpleBody>
            <PurpleBody>
              You will find an explanation of our decision in your adverse action notice, which will
              be delivered to you via email and added to your{` `}
              <HLink
                $css={css`
                  ${TYPOGRAPHY.S}
                  color: inherit;
                `}
                onClick={handleDashboardClick}
              >
                dashboard
              </HLink>
              {'.'}
            </PurpleBody>
          </PurpleBox>
          <FPayCSWrapper>
            <HeaderBlock>
              <HdrTop>However, you may qualify for a personal loan through Fiona!</HdrTop>
              <P
                $css={css`
                  ${TYPOGRAPHY.M}
                `}
              >
                Fiona helps you compare loan offers from top providers, to find the one that’s right
                for you!
              </P>
            </HeaderBlock>
            <FContainer>
              <BulletsBody>
                <Div
                  $css={css`
                    padding: ${SPACINGS.XL}px 0;
                  `}
                >
                  <img alt="fiona" width="133px" height="40px" src={fiona} />
                </Div>
                {StaticBullets()}
                <ButtonContainer>
                  <Button
                    type="button"
                    useContainerWidth
                    rounded
                    variant="primary"
                    onClick={redirectToFiona}
                  >
                    Get Started
                  </Button>
                </ButtonContainer>
              </BulletsBody>
              <Img alt="" src={phoneguy} />
            </FContainer>
          </FPayCSWrapper>
          <Legal>
            Fiona and Figure are independent companies who have created this advertisement. Click
            the button above to enter the Fiona website and see if you can qualify for a
            personalized loan offer from Fiona.
          </Legal>
        </Content>
      </BaseContainer>
    </OnboardingThemeContextProvider>
  );
};

export default DeclineFionaContainer;
